import axios from "axios";
import { AUTH_ENDPOINTS, API_BASE_URL } from "../constants/apis";

// Token refresh service
class TokenRefreshService {
    constructor() {
        this.isRefreshing = false;
        this.failedQueue = [];
    }

    // Refresh the access token
    async refreshToken() {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH}`, {}, {
                headers: {
                    'X-Refresh-Token': refreshToken
                }
            });

            const { access_token, refresh_token } = response.data;

            // Update tokens in localStorage
            localStorage.setItem('accessToken', access_token);
            if (refresh_token) {
                localStorage.setItem('refreshToken', refresh_token);
            }

            return access_token;
        } catch (error) {
            console.error('Token refresh failed:', error);
            // Clear tokens and redirect to login
            this.clearTokens();
            throw error;
        }
    }

    // Process failed requests after token refresh
    processQueue(error, token = null) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });

        this.failedQueue = [];
    }

    // Add request to queue while refreshing
    addToQueue(resolve, reject) {
        this.failedQueue.push({ resolve, reject });
    }

    // Clear all tokens
    clearTokens() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('token'); // Legacy token
    }

    // Check if token is expired
    isTokenExpired(token) {
        if (!token) return true;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            return payload.exp < currentTime;
        } catch (error) {
            return true;
        }
    }

    // Get valid token (refresh if needed)
    async getValidToken() {
        let token = localStorage.getItem('accessToken');

        if (!token || this.isTokenExpired(token)) {
            if (this.isRefreshing) {
                // Wait for ongoing refresh
                return new Promise((resolve, reject) => {
                    this.addToQueue(resolve, reject);
                });
            }

            this.isRefreshing = true;

            try {
                token = await this.refreshToken();
                this.processQueue(null, token);
                return token;
            } catch (error) {
                this.processQueue(error, null);
                throw error;
            } finally {
                this.isRefreshing = false;
            }
        }

        return token;
    }
}

// Create singleton instance
const tokenRefreshService = new TokenRefreshService();

// Axios interceptor for automatic token refresh
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const newToken = await tokenRefreshService.getValidToken();
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return axios(originalRequest);
            } catch (refreshError) {
                // Redirect to login or handle authentication failure
                console.error('Authentication failed:', refreshError);
                tokenRefreshService.clearTokens();
                window.location.href = '/signin';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default tokenRefreshService;
